/** Fill the output frame without stretching, cropping overflow around the center. */
export function drawSourceCover(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  outputWidth: number,
  outputHeight: number,
  sourceWidth: number,
  sourceHeight: number,
  backgroundColor = '#000000',
): void {
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.clearRect(0, 0, outputWidth, outputHeight)
  ctx.fillStyle = '#000000'
  if (backgroundColor.trim()) {
    // Invalid CSS colors leave the canvas fillStyle unchanged, preserving the black fallback.
    ctx.fillStyle = backgroundColor
  }
  ctx.fillRect(0, 0, outputWidth, outputHeight)

  const scale = Math.max(outputWidth / sourceWidth, outputHeight / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale
  const x = (outputWidth - width) / 2
  const y = (outputHeight - height) / 2

  ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, x, y, width, height)
}

export type CanvasPosterOptions = {
  type?: 'image/png' | 'image/jpeg' | 'image/webp'
  quality?: number
  /** Maximum time to wait for browsers that never invoke canvas.toBlob. */
  timeoutMs?: number
}

/** Create a browser-owned URL for a still image of the composited Canvas frame. */
export function createCanvasPosterUrl(
  canvas: HTMLCanvasElement,
  options: CanvasPosterOptions = {},
): Promise<string | null> {
  if (typeof canvas.toBlob !== 'function') {
    return Promise.resolve(null)
  }

  const type = options.type ?? 'image/png'
  const timeoutMs = Math.max(1, options.timeoutMs ?? 8_000)

  return new Promise((resolve) => {
    let settled = false
    const finish = (value: string | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => finish(null), timeoutMs)

    try {
      canvas.toBlob((blob) => {
        if (settled) return
        if (!blob) {
          finish(null)
          return
        }
        try {
          finish(URL.createObjectURL(blob))
        } catch {
          finish(null)
        }
      }, type, options.quality)
    } catch {
      finish(null)
    }
  })
}
